import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CardContent,
  Divider,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';

import { GcpEncryptionComponent } from '@/components/encryption/gcpEncryptionComponent';
import { TagsSection } from '@/components/tags/tagsSection';
import { DiskSettingsSection } from '@/components/volumes/gcpDiskSettings';
import type { StepProps } from '@/components/wizard/StepProps';

import type {
  GcpDiskConfigState,
  RestoreGcpVMState,
} from '../../restoreGcpVMWizard';

export const SingleGcpDiskConfig = ({
  accountID,
  region,
  zone,
  machineType,
  diskId,
  initialSettings,
  diskSettings,
  setWizardState,
  showTags = true,
  showEncryption = false,
}: {
  accountID: string;
  region: string;
  zone: string;
  machineType: string;
  diskId: string;
  initialSettings: GcpDiskConfigState;
  diskSettings: GcpDiskConfigState;
  setWizardState: StepProps<RestoreGcpVMState>['setWizardState'];
  showTags?: boolean;
  showEncryption?: boolean;
}) => {
  const setVolumesConfigState = <T extends keyof GcpDiskConfigState>(
    field: T,
    value: GcpDiskConfigState[T]
  ) => {
    setWizardState((state) => {
      const disksState = state.disksState.map((d) =>
        d.diskSettings.diskId === diskId ? { ...d, [field]: value } : d
      );
      return { ...state, disksState };
    });
  };

  return (
    <Accordion defaultExpanded={false} className='my-[2px]'>
      <AccordionSummary>
        <Typography variant='body1'>
          {
            (diskSettings.keepOriginalSettings ? initialSettings : diskSettings)
              .diskSettings.diskName
          }
        </Typography>
      </AccordionSummary>
      <AccordionDetails className='p-0'>
        <Box sx={showEncryption ? {} : { display: 'none' }}>
          <CardContent className='p-0'>
            <GcpEncryptionComponent
              title='Encrypt this disk'
              cloudAccountId={accountID}
              regionName={region}
              onChange={(isEncrypted, encryptionKey) =>
                setVolumesConfigState('encryptionKey', encryptionKey)
              }
            />
          </CardContent>
        </Box>
        {showEncryption && <Divider />}
        <CardContent className='p-0'>
          <DiskSettingsSection
            initialSettings={initialSettings.diskSettings}
            settings={diskSettings.diskSettings}
            keepOriginalSettings={diskSettings.keepOriginalSettings}
            accountId={accountID}
            zone={zone}
            machineType={machineType}
            onChange={(diskSettings) =>
              setVolumesConfigState('diskSettings', diskSettings)
            }
            setKeepOriginalSettings={(value: boolean) =>
              setVolumesConfigState('keepOriginalSettings', value)
            }
          />
        </CardContent>
        {showTags && (
          <>
            <Divider />
            <CardContent className='px-0'>
              <TagsSection
                initialTags={initialSettings.tags || {}}
                tags={diskSettings.tags || initialSettings.tags || {}}
                keepOriginalTags={diskSettings.keepOriginalTags}
                setKeepOriginalTags={(keepOriginalTags: boolean) =>
                  setVolumesConfigState('keepOriginalTags', keepOriginalTags)
                }
                onChange={(tags) => setVolumesConfigState('tags', tags)}
                provider={Provider.Gcp}
              />
            </CardContent>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
